/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { Container, Box, FormControlLabel, Switch, Accordion, AccordionDetails, AccordionSummary, FormGroup, Grid, TextField, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Brush } from 'recharts';
import { ArrowDownward } from '@mui/icons-material';
import { IReadingsInstruments } from '../../../services/api/reading/ReadingServices';
import { formatDate, helpers } from '../../../services/helpers/helpers';

interface IAllInstrumentProps {
    value: IReadingsInstruments[],
    type: string,
    yAxisName: string,
}

export const AllInstrument = ({ value, type, yAxisName }: IAllInstrumentProps) => {

    if (!value || !Array.isArray(value)) {
        return null;
    }

    const data = value.filter(index => index && index.tipo_instrumento === type);

    if (data.length === 0) {
        return null;
    }

    const allDates = new Set<string>();
    data.forEach(instrument => {
        if (instrument && instrument.leituras && Array.isArray(instrument.leituras)) {
            instrument.leituras.forEach(leitura => {
                if (leitura && leitura.data_leitura) {
                    allDates.add(leitura.data_leitura);
                }
            });
        }
    });

    const [transformedData, setTransformedData] = useState<any[]>([]);

    useEffect(() => {
        const tempTransformedData: any[] = [];
        data.forEach(instrument => {
            if (instrument && instrument.leituras && Array.isArray(instrument.leituras)) {
                instrument.leituras.forEach(leitura => {
                    if (leitura) {
                        const dataLeitura = new Date(leitura.data_leitura).toISOString().split('T')[0];
                        let existingEntry = tempTransformedData.find(entry => entry.data_leitura === dataLeitura);
                        if (!existingEntry) {
                            existingEntry = { data_leitura: dataLeitura };
                            tempTransformedData.push(existingEntry);
                        }

                        if (type === 'vazao' && leitura.vazao !== undefined && leitura.vazao !== null) {
                            existingEntry[instrument.nome] = leitura.vazao;
                        } else if (type === 'regua' && leitura.borda_livre !== undefined && leitura.borda_livre !== null) {
                            existingEntry[instrument.nome] = leitura.borda_livre;
                        } else if (type === 're' && leitura.angulo !== undefined && leitura.angulo !== null) {
                            existingEntry[instrument.nome] = leitura.angulo;
                        } else if (type === 'inclinometro' && leitura.deslocamento !== undefined && leitura.deslocamento !== null) {
                            existingEntry[instrument.nome] = leitura.deslocamento;
                        } else if (leitura.cota !== undefined && leitura.cota !== null) {
                            existingEntry[instrument.nome] = parseFloat(leitura.cota.replace(',', '.'));
                        }
                    }
                });
            }
        });
        setTransformedData(tempTransformedData);
    }, [data, type]);

    console.log('dataTeste', transformedData);

    const [activeInstruments, setActiveInstruments] = useState<string[]>(data.map(index => index.uuid));
    useEffect(() => {
        setActiveInstruments(data.map(index => index.uuid));
    }, [data]);


    const handleToggle = (uuid: string) => {
        setActiveInstruments(prevState =>
            prevState.includes(uuid)
                ? prevState.filter(id => id !== uuid)
                : [...prevState, uuid]
        );
    };

    const [dotSize, setDotSize] = useState(3);
    const [lineSize, setLineSize] = useState(2);
    const [showDot, setShowDot] = useState(true);
    const [layout, setLayout] = useState<'horizontal' | 'vertical'>('horizontal');
    const [align, setAlign] = useState<'center' | 'left' | 'right'>('center');
    const [verticalAlign, setVerticalAlign] = useState<'bottom' | 'middle' | 'top'>('bottom');

    const handleChangeShowDot = () => {
        setShowDot(!showDot);
    };

    const handleDotSizeChange = (event: any) => {
        setDotSize(Number(event.target.value));
    };

    const handleLineSizeChange = (event: any) => {
        setLineSize(Number(event.target.value));
    };

    const handleLayoutChange = (event: any) => {
        setLayout(event.target.value);
    };

    const handleAlignChange = (event: any) => {
        setAlign(event.target.value);
    };

    const handleVerticalAlignChange = (event: any) => {
        setVerticalAlign(event.target.value);
    };

    return (
        <Container>
            <Box width={'100%'} justifyContent={'center'} alignContent={'center'} padding={2} gap={1}>
                <Box width={'100%'}>
                    {transformedData.length > 0 ? (
                        <ResponsiveContainer width='100%' height={300}>
                            <LineChart data={transformedData}>
                                <CartesianGrid strokeDasharray='3 3' />
                                <XAxis dataKey="data_leitura" tickFormatter={helpers.formatXAxisDate} />
                                <YAxis label={{ value: yAxisName, angle: -90, position: 'insideLeft', dy: 60, dx: -30 }} domain={['auto, auto']} tickFormatter={(value) => value.toFixed(2)}/>
                                <Tooltip />
                                <Legend layout={layout} verticalAlign={verticalAlign} align={align} />
                                <Brush dataKey={'data_leitura'} height={25} />
                                {data
                                    .filter(index => activeInstruments.includes(index.uuid))
                                    .map(index => (
                                        <Line
                                            key={index.uuid}
                                            type='monotone'
                                            dataKey={index.nome}
                                            name={index.nome}
                                            stroke={`#${Math.floor(Math.random() * 16777215).toString(16)}`}
                                            strokeWidth={lineSize}
                                            dot={showDot ? { strokeWidth: dotSize } : false}
                                        />
                                    ))}
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <p>Nenhum dado para exibir o gráfico.</p>
                    )}
                </Box>
                <Box width={'100%'}>
                    <Accordion>
                        <AccordionSummary expandIcon={<ArrowDownward />}>
                            Personalização do Gráfico
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box marginTop={5}>
                                <Grid container spacing={2} padding={2}>
                                    <Grid item container alignItems={'center'} spacing={2} justifyContent={'center'}>
                                        <Grid item xs={4}>
                                            <TextField
                                                label='Tamanho do Marcador'
                                                value={dotSize}
                                                type='number'
                                                variant='filled'
                                                onChange={handleDotSizeChange}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                label='Tamanho da linha'
                                                value={lineSize}
                                                type='number'
                                                variant='filled'
                                                onChange={handleLineSizeChange}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <FormGroup>
                                                <FormControlLabel control={<Switch checked={showDot} onChange={handleChangeShowDot} />} label="Mostrar Marcador" />
                                            </FormGroup>
                                        </Grid>
                                    </Grid>
                                    <Grid item container spacing={2}>
                                        <Grid item xs={4}>
                                            <FormControl fullWidth>
                                                <InputLabel>Tipo de legenda</InputLabel>
                                                <Select value={layout} onChange={handleLayoutChange} label='Tipo de legenda'>
                                                    <MenuItem value={'vertical'}>Vertical</MenuItem>
                                                    <MenuItem value={'horizontal'}>Horizontal</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <FormControl fullWidth>
                                                <InputLabel>Alinhamento X de legenda</InputLabel>
                                                <Select value={align} onChange={handleAlignChange} label='Alinhamento X de legenda'>
                                                    <MenuItem value={'center'}>Central</MenuItem>
                                                    <MenuItem value={'left'}>Esquerda</MenuItem>
                                                    <MenuItem value={'right'}>Direita</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <FormControl fullWidth>
                                                <InputLabel>Alinhamento Y de legenda</InputLabel>
                                                <Select value={verticalAlign} onChange={handleVerticalAlignChange} label='Alinhamento Y de legenda'>
                                                    <MenuItem value={'bottom'}>Baixo</MenuItem>
                                                    <MenuItem value={'top'}>Cima</MenuItem>
                                                    <MenuItem value={'middle'}>Meio</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Accordion>
                                                <AccordionSummary expandIcon={<ArrowDownward />}>
                                                    Instrumentos Mostrados
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Box display="flex" flexDirection="column">
                                                        {data.map(index => (
                                                            <FormControlLabel
                                                                key={index.uuid}
                                                                control={
                                                                    <Switch
                                                                        checked={activeInstruments.includes(index.uuid)}
                                                                        onChange={() => handleToggle(index.uuid)}
                                                                        color="primary"
                                                                    />
                                                                }
                                                                label={index.nome}
                                                            />
                                                        ))}
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Box>
        </Container>
    );
};